import React, { useEffect, useState, useCallback } from "react";
import { useForm } from "@tanstack/react-form";
import { CheckCircle2, Trash2 } from "lucide-react";
import { balanceBillSchema } from "@/lib/form-schema";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPortal,
} from "@/components/ui/dialog";
import { InputWithLabel } from "../input-label";
import TextAreaInput from "../input-textarea";
import { DialogClose, DialogTitle } from "@radix-ui/react-dialog";
import InputDate from "../input-date";
import { formatDateToMMDDYYYY } from "@/lib/date-utils";
import type { SelectedImageObjectType } from "@/types";
import { useBalanceBillUpload } from "@/hooks/use-balance-bill";
import type {
  BalanceBillItemResponse,
  BalanceBillUploadResponseData,
} from "@/model/balance-bill";
import type { IUserInfo } from "@/hooks/use-user-info";
import useUserInfo from "@/hooks/use-user-info";
import { useQueryClient } from "@tanstack/react-query";
import { QueryKeys, STORAGE, STORAGE_KEY, USER_MODE } from "@/constants";
import type { RootState } from "@/store/store";
import { useSelector } from "react-redux";
import { Label } from "@/components/ui/label";
import type { MESSAGE_ICON_TYPE } from "@/constants/message-icon";
import { MESSAGE_ICON } from "@/constants/message-icon";
import { AppAlertWithIconDialog } from "../app-dialog/app-dialog-with-icon";
import { DEFAULT_MESSAGE, getErrorMessage } from "@/lib/error-message-utils";
import { getItem } from "@/lib/storage-utils";
import { lowerCaseComparison } from "@/lib/string-util";

type BalanceBillUploadDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedBill: SelectedImageObjectType[];
};

type BalanceBillUploadFormValues = {
  claimNumber: string;
  balanceBillAmt: string;
  billedAmt: string;
  patientName: string;
  providerName: string;
  additionalInformation: string;
  dateOfService: Date | undefined;
};

const BalanceBillUploadDialog: React.FC<BalanceBillUploadDialogProps> = ({
  open,
  onOpenChange,
  selectedBill,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<SelectedImageObjectType[]>(
    selectedBill ?? [],
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileError, setFileError] = useState<string>("");
  const [openSubmissionAlert, setOpenSubmissionAlert] = useState({
    iconType: MESSAGE_ICON.ERROR as MESSAGE_ICON_TYPE,
    open: false,
    message: "Something went wrong",
    subMessage: "Please try again later.",
  });

  const authData = getItem(STORAGE_KEY.AUTH_DATA, STORAGE.SESSION);
  const { activeCoverage } = useSelector(
    (state: RootState) => state.planCoverageInfo,
  );
  const userInfo = useUserInfo();
  const balanceBillUploadMutation = useBalanceBillUpload();
  const queryClient = useQueryClient();

  const form = useForm({
    defaultValues: {
      claimNumber: "",
      balanceBillAmt: "",
      billedAmt: "",
      patientName: "",
      providerName: "",
      additionalInformation: "",
      dateOfService: undefined,
    },

    onSubmit: ({ value }) => {
      if (selectedFiles.length === 0) {
        setFileError("At least one file is required");
        return;
      }

      setFileError(""); // Clear error

      setIsSubmitting(true);
      const formData = buildBalanceBillFormData(userInfo, value);

      balanceBillUploadMutation.mutate(
        {
          formData,
          token: userInfo.accessToken,
        },
        {
          onSuccess: (newItem: BalanceBillItemResponse) => {
            // push newly created ticket into query cache
            if (newItem?.data?.ticketId) {
              queryClient.setQueryData(
                [
                  QueryKeys.BALANCE_BILL_LIST,
                  activeCoverage?.claimCoverageCode,
                  activeCoverage?.planId,
                ],
                (old: BalanceBillUploadResponseData[] = []) => [
                  newItem?.data,
                  ...old,
                ],
              );
            }
            setIsSubmitting(false);
            form.reset();
            onOpenChange(false);

            if (newItem?.summary?.status_code === "REQ-200") {
              setOpenSubmissionAlert({
                iconType: MESSAGE_ICON.SUCCESS,
                open: true,
                message: "Balance Bill Submitted",
                subMessage: "Your details and photos were received.",
              });
            } else {
              setOpenSubmissionAlert({
                iconType: MESSAGE_ICON.ERROR,
                open: true,
                message: "Submission Failed",
                subMessage:
                  "Your submission has failed. Please try again later.",
              });
            }
          },

          onError: (err) => {
            setIsSubmitting(false);
            form.reset();
            onOpenChange(false);

            setOpenSubmissionAlert({
              iconType: MESSAGE_ICON.ERROR,
              open: true,
              message: DEFAULT_MESSAGE.ERROR.MESSAGE,
              subMessage: getErrorMessage(err),
            });
          },
        },
      );
    },
  });

  useEffect(() => {
    setSelectedFiles(selectedBill ?? []);
  }, [selectedBill]);

  // Prevent page reload/navigation during submission
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isSubmitting) {
        e.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isSubmitting]);

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && isSubmitting) {
        return;
      }

      if (!newOpen) {
        form.reset();
        setSelectedFiles([]);
        setFileError("");
      }

      onOpenChange(newOpen);
    },
    [isSubmitting, onOpenChange, form],
  );

  function buildBalanceBillFormData(
    userInfo: IUserInfo,
    requestData: BalanceBillUploadFormValues,
  ) {
    const formData = new FormData();

    formData.append("groupId", userInfo?.groupId);
    formData.append("divisionId", userInfo?.divisionId);
    formData.append("planId", activeCoverage?.planId);
    formData.append("memberId", userInfo?.memberId);
    formData.append("claimCoverageCode", activeCoverage?.claimCoverageCode);

    formData.append("claimNumber", requestData.claimNumber);
    formData.append("balanceBillAmt", requestData.balanceBillAmt);
    formData.append("billedAmt", requestData.billedAmt);
    formData.append("patientName", requestData.patientName);
    formData.append(
      "dateOfService",
      String(formatDateToMMDDYYYY(requestData.dateOfService)),
    );
    formData.append("providerName", requestData.providerName);
    formData.append("additionalInformation", requestData.additionalInformation);

    selectedFiles.forEach((file) => {
      if (file.image instanceof File) {
        formData.append("attachments", file.image);
      }
    });

    return formData;
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogPortal>
          <DialogContent
            className="flex h-3/4 max-w-[45rem] flex-col p-0"
            onEscapeKeyDown={(e) => {
              if (isSubmitting) {
                e.preventDefault();
              }
            }}
            onPointerDownOutside={(e) => {
              if (isSubmitting) {
                e.preventDefault();
              }
            }}
            onInteractOutside={(e) => {
              if (isSubmitting) {
                e.preventDefault();
              }
            }}
          >
            <DialogHeader className="border-b px-6 py-4">
              <DialogTitle className="font-semibold">
                Balance Bill Details
              </DialogTitle>
            </DialogHeader>

            <form
              method="POST"
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                form.handleSubmit();
              }}
              className="flex min-h-0 flex-1 flex-col"
            >
              <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
                <div className="flex h-fit flex-col gap-5">
                  {selectedFiles.length > 0 ? (
                    <div className="flex flex-col gap-5">
                      <Label className="flex items-center gap-2">
                        <CheckCircle2 color="green" />
                        <span>{selectedFiles.length} files attached. </span>
                      </Label>

                      <div className="grid grid-cols-1 gap-2">
                        {selectedFiles.map((file, index) => (
                          <div
                            key={index}
                            className="relative flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3"
                          >
                            {/* File info */}
                            <div className="flex-1 overflow-hidden">
                              <p className="truncate text-sm font-medium">
                                {file.image?.name}
                              </p>
                            </div>

                            {/* Remove button - disabled during submission */}
                            <button
                              type="button"
                              title="delete"
                              onClick={() => {
                                setSelectedFiles((files) =>
                                  files.filter((_, i) => i !== index),
                                );

                                setFileError("");
                              }}
                              disabled={isSubmitting}
                              className="text-gray-400 hover:text-red-500 focus-visible:outline-offset-2 focus-visible:outline-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <p
                        className={`text-sm ${fileError ? "text-red-500" : "text-gray-500"}`}
                      >
                        {fileError || "No files selected"}
                      </p>
                      {fileError && (
                        <p className="text-xs text-red-500">
                          * Please upload at least one file
                        </p>
                      )}
                    </div>
                  )}

                  <hr />

                  <div className="flex flex-col gap-3">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <form.Field
                        name="claimNumber"
                        validators={{
                          onChange: ({ value }) => {
                            const parsedValue =
                              balanceBillSchema.shape.claimNumber.safeParse(
                                value,
                              );
                            return parsedValue.success
                              ? undefined
                              : parsedValue.error.errors[0].message;
                          },
                        }}
                      >
                        {(field) => (
                          <InputWithLabel
                            label="Claim Number*"
                            id="claim_number"
                            placeholder="Enter Claim Number"
                            value={field.state.value}
                            onChange={(newValue) =>
                              field.handleChange(newValue)
                            }
                            onBlur={() => field.handleBlur()}
                            error={field.state.meta.errors[0]}
                            isDisabled={isSubmitting}
                          />
                        )}
                      </form.Field>

                      <form.Field
                        name="balanceBillAmt"
                        validators={{
                          onChange: ({ value }) => {
                            const parsedValue =
                              balanceBillSchema.shape.balanceBillAmt.safeParse(
                                value,
                              );
                            return parsedValue.success
                              ? undefined
                              : parsedValue.error.errors[0].message;
                          },
                        }}
                      >
                        {(field) => (
                          <InputWithLabel
                            label="Balance Bill Amount*"
                            id="balance_bill_amount"
                            placeholder="Enter Amount"
                            value={field.state.value}
                            onChange={(newValue) => {
                              // Only allow upto 9 numbers and decimal with max 2 places
                              if (
                                newValue === "" ||
                                /^\d{0,9}(\.\d{0,2})?$/.test(newValue)
                              ) {
                                field.handleChange(newValue);
                              }
                            }}
                            onBlur={() => {
                              if (
                                field.state.value &&
                                field.state.value !== "."
                              ) {
                                const num = parseFloat(field.state.value);
                                if (!isNaN(num) && num >= 0) {
                                  field.handleChange(num.toFixed(2));
                                }
                              }
                              field.handleBlur();
                            }}
                            // onBlur={() => field.handleBlur()}
                            error={field.state.meta.errors[0]}
                            isDisabled={isSubmitting}
                          />
                        )}
                      </form.Field>

                      <form.Field
                        name="billedAmt"
                        validators={{
                          onChange: ({ value }) => {
                            const parsedValue =
                              balanceBillSchema.shape.billedAmt.safeParse(
                                value,
                              );
                            return parsedValue.success
                              ? undefined
                              : parsedValue.error.errors[0].message;
                          },
                        }}
                      >
                        {(field) => (
                          <InputWithLabel
                            label="Amount Billed*"
                            id="amount_billed"
                            placeholder="Enter Amount"
                            value={field.state.value}
                            onChange={(newValue) => {
                              if (
                                newValue === "" ||
                                /^\d{0,9}(\.\d{0,2})?$/.test(newValue)
                              ) {
                                field.handleChange(newValue);
                              }
                            }}
                            onBlur={() => {
                              if (
                                field.state.value &&
                                field.state.value !== "."
                              ) {
                                const num = parseFloat(field.state.value);
                                if (!isNaN(num) && num >= 0) {
                                  field.handleChange(num.toFixed(2));
                                }
                              }
                              field.handleBlur();
                            }}
                            error={field.state.meta.errors[0]}
                            isDisabled={isSubmitting}
                          />
                        )}
                      </form.Field>
                      <form.Field
                        name="patientName"
                        validators={{
                          onChange: ({ value }) => {
                            const parsedValue =
                              balanceBillSchema.shape.patientName.safeParse(
                                value,
                              );
                            return parsedValue.success
                              ? undefined
                              : parsedValue.error.errors[0].message;
                          },
                        }}
                      >
                        {(field) => (
                          <InputWithLabel
                            label="Patient Name*"
                            id="patient_name"
                            placeholder="Enter Name"
                            value={field.state.value}
                            onChange={(newValue) =>
                              field.handleChange(newValue)
                            }
                            onBlur={() => field.handleBlur()}
                            error={field.state.meta.errors[0]}
                            isDisabled={isSubmitting}
                          />
                        )}
                      </form.Field>
                      <form.Field
                        name="dateOfService"
                        validators={{
                          onChange: ({ value }) => {
                            const parsedValue =
                              balanceBillSchema.shape.dateOfService.safeParse(
                                value,
                              );
                            return parsedValue.success
                              ? undefined
                              : parsedValue.error.errors[0].message;
                          },
                        }}
                      >
                        {(field) => (
                          <InputDate
                            id="date_of_service"
                            label="Date of Service*"
                            placeholder="Date of Service"
                            value={field.state.value}
                            onChange={(newDate) => field.handleChange(newDate)}
                            error={field.state.meta.errors[0]}
                            disabled={isSubmitting}
                          />
                        )}
                      </form.Field>
                      <form.Field
                        name="providerName"
                        validators={{
                          onChange: ({ value }) => {
                            const parsedValue =
                              balanceBillSchema.shape.providerName.safeParse(
                                value,
                              );
                            return parsedValue.success
                              ? undefined
                              : parsedValue.error.errors[0].message;
                          },
                        }}
                      >
                        {(field) => (
                          <InputWithLabel
                            label="Provider*"
                            id="provider"
                            placeholder="Enter Provider"
                            value={field.state.value}
                            onChange={(newValue) =>
                              field.handleChange(newValue)
                            }
                            onBlur={() => field.handleBlur()}
                            error={field.state.meta.errors[0]}
                            isDisabled={isSubmitting}
                          />
                        )}
                      </form.Field>
                    </div>

                    <div className="flex flex-col gap-3">
                      <form.Field
                        name="additionalInformation"
                        validators={{
                          onChange: ({ value }) => {
                            const parsedValue =
                              balanceBillSchema.shape.additionalInformation.safeParse(
                                value,
                              );
                            return parsedValue.success
                              ? undefined
                              : parsedValue.error.errors[0].message;
                          },
                        }}
                      >
                        {(field) => (
                          <>
                            <Label>Additional Fields (Optional)</Label>
                            <TextAreaInput
                              id="additional_information"
                              className="min-h-48 resize-none border-0 bg-gray-100"
                              placeholder="Enter Additional Info"
                              value={field.state.value}
                              onChange={(newValue) =>
                                field.handleChange(newValue)
                              }
                              onBlur={() => field.handleBlur()}
                              error={field.state.meta.errors[0]}
                              disabled={isSubmitting}
                            />
                          </>
                        )}
                      </form.Field>
                    </div>

                    <p className="text-xs">*Required Fields</p>
                  </div>
                </div>
              </div>

              <DialogFooter className="flex flex-col gap-4 border-t px-6 py-4 sm:flex-row">
                <DialogClose asChild className="order-2 sm:order-1">
                  <Button
                    variant="outline"
                    type="button"
                    className="min-w-32"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                </DialogClose>

                <form.Subscribe selector={(state) => [state.canSubmit]}>
                  {([canSubmit]) => (
                    <Button
                      variant="default"
                      className="order-1 min-w-32 bg-primary hover:bg-primary-selected sm:order-2"
                      type="submit"
                      disabled={
                        !canSubmit ||
                        isSubmitting ||
                        selectedFiles.length === 0 ||
                        lowerCaseComparison(authData?.mode, USER_MODE.GUEST)
                      }
                    >
                      {isSubmitting ? "Submitting ..." : "Submit"}
                    </Button>
                  )}
                </form.Subscribe>
              </DialogFooter>
            </form>
          </DialogContent>
        </DialogPortal>
      </Dialog>

      <AppAlertWithIconDialog
        open={openSubmissionAlert.open}
        iconType={openSubmissionAlert.iconType}
        message={openSubmissionAlert.message}
        subMessage={openSubmissionAlert.subMessage}
        onOpenChange={(open) =>
          setOpenSubmissionAlert((prev) => ({ ...prev, open }))
        }
      />
    </>
  );
};

export default BalanceBillUploadDialog;
